import React, { useState, useCallback, Suspense } from 'react';
import { createPortal } from 'react-dom';

// Lazy load the BookingForm component
const BookingForm = React.lazy(() => import('./BookingForm'));

const BookingFormModal = ({ isOpen, onClose, isDarkMode = false }) => {
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        // Add a small delay for smooth closing animation
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 200);
    }, [onClose]);

    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    }, [handleClose]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    }, [handleClose]);

    if (!isOpen) return null;

    return createPortal(
        <div 
            className={`modal-backdrop ${isClosing ? 'closing' : ''}`}
            onClick={handleBackdropClick}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px',
                opacity: isClosing ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out'
            }}
        >
            <div 
                className="modal-content"
                style={{
                    backgroundColor: isDarkMode ? '#1f2937' : 'white',
                    borderRadius: '12px',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    position: 'relative',
                    transform: isClosing ? 'scale(0.95)' : 'scale(1)',
                    transition: 'transform 0.2s ease-in-out',
                    boxShadow: isDarkMode ? '0 20px 60px rgba(0, 0, 0, 0.5)' : '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
            >
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: isDarkMode ? '#d1d5db' : '#666',
                        zIndex: 1,
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    aria-label="Close modal"
                >
                    ×
                </button>

               
                {/* Modal Body */}
                <div >
                    <Suspense fallback={
                        <SkeletonForm isDarkMode={isDarkMode} />
                    }>
                        {/* <SkeletonForm /> */}
                        <BookingForm />
                    </Suspense>
                </div>
            </div>

            <style jsx>{`
                @keyframes skeletonPulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.4;
                    }
                }
            `}</style>
        </div>,
        document.body
    );
};

const SkeletonForm = ({ isDarkMode = false }) => {
    const skeletonColor = isDarkMode ? '#374151' : '#e2e8f0';
    
    return (
<div>
                            {/* Skeleton Form Grid */}
                            <div style={{ display: 'grid', gap: '20px', padding: '30px' }}>
                                {/* Row 1: Name and Email */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <div style={{
                                            height: '14px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '4px',
                                            marginBottom: '8px',
                                            width: '60%',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                        <div style={{
                                            height: '44px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '8px',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                    </div>
                                    <div>
                                        <div style={{
                                            height: '14px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '4px',
                                            marginBottom: '8px',
                                            width: '50%',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                        <div style={{
                                            height: '44px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '8px',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                    </div>
                                </div>

                                {/* Row 2: Phone and Date */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <div style={{
                                            height: '14px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '4px',
                                            marginBottom: '8px',
                                            width: '40%',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                        <div style={{
                                            height: '44px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '8px',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                    </div>
                                    <div>
                                        <div style={{
                                            height: '14px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '4px',
                                            marginBottom: '8px',
                                            width: '45%',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                        <div style={{
                                            height: '44px',
                                            backgroundColor: skeletonColor,
                                            borderRadius: '8px',
                                            animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                        }}></div>
                                    </div>
                                </div>

                                {/* Row 3: Pickup Location */}
                                <div>
                                    <div style={{
                                        height: '14px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '4px',
                                        marginBottom: '8px',
                                        width: '35%',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                    <div style={{
                                        height: '44px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '8px',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                </div>

                                {/* Row 4: Drop-off Location */}
                                <div>
                                    <div style={{
                                        height: '14px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '4px',
                                        marginBottom: '8px',
                                        width: '40%',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                    <div style={{
                                        height: '44px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '8px',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                </div>

                                {/* Row 5: Message */}
                                <div>
                                    <div style={{
                                        height: '14px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '4px',
                                        marginBottom: '8px',
                                        width: '30%',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                    <div style={{
                                        height: '80px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '8px',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                </div>

                                {/* Submit Button */}
                                <div style={{ marginTop: '20px' }}>
                                    <div style={{
                                        height: '48px',
                                        backgroundColor: skeletonColor,
                                        borderRadius: '8px',
                                        animation: 'skeletonPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                </div>
                            </div>
                        </div>
    )
}

export default BookingFormModal;
